/**
 * Average-speed proposal for distance/duration metric templates.
 *
 * Any workout template that captures a distance in km AND a duration in minutes
 * (bike, e-bike, cardio, running, …) can derive average speed in km/h. Rather
 * than making the athlete do the arithmetic, we propose the computed value as
 * soon as both inputs are present — and step aside permanently the moment they
 * type their own number (a bike computer's reading beats our division).
 *
 * Field keys are NOT consistent across templates (`distance_km` vs `distance`,
 * `duration_min` vs `duration`, `avg_speed_kmh` vs `avg_speed`), so fields are
 * matched on unit first and key shape second.
 */
import { useEffect, useRef } from 'react'

const KM_UNITS = ['km']
const MIN_UNITS = ['min', 'minutes', 'mins']
const SPEED_UNITS = ['km/h', 'kmh', 'kph']

function normUnit(unit) {
  return String(unit || '').trim().toLowerCase()
}

function normKey(key) {
  return String(key || '').trim().toLowerCase()
}

/**
 * Locate the distance-in-km, duration-in-minutes and average-speed fields in a
 * metric field template. Returns null for any role the template lacks.
 *
 * @param {Array<{key: string, unit?: string}>} fields
 * @returns {{distanceKey: ?string, durationKey: ?string, speedKey: ?string}}
 */
export function findSpeedFields(fields) {
  const list = Array.isArray(fields) ? fields : []

  const match = (units, keyTest, keyExclude) => {
    const byUnit = list.find((f) => units.includes(normUnit(f?.unit)) && !keyExclude?.(normKey(f?.key)))
    if (byUnit) return byUnit.key
    const byKey = list.find((f) => keyTest(normKey(f?.key)))
    return byKey ? byKey.key : null
  }

  // Speed first: its "km/h" unit contains "km", so distance matching must not
  // swallow it.
  const speedKey = match(SPEED_UNITS, (k) => k.includes('speed'))
  const isSpeed = (k) => k.includes('speed')

  return {
    distanceKey: match(KM_UNITS, (k) => k.includes('distance_km') || k === 'distance', isSpeed),
    durationKey: match(MIN_UNITS, (k) => k.includes('duration'), isSpeed),
    speedKey,
  }
}

/**
 * Average speed in km/h from a distance in km and a duration in minutes.
 * Returns null when either input is missing, non-numeric or non-positive.
 *
 * @returns {?number} rounded to one decimal
 */
export function computeAvgSpeedKmh(distanceKm, durationMin) {
  const distance = Number(distanceKm)
  const duration = Number(durationMin)
  if (
    distanceKm === '' || distanceKm == null || durationMin === '' || durationMin == null ||
    !Number.isFinite(distance) || !Number.isFinite(duration) ||
    distance <= 0 || duration <= 0
  ) {
    return null
  }
  return Math.round((distance / (duration / 60)) * 10) / 10
}

/**
 * Keep a template's average-speed field in step with its distance and duration
 * fields until the athlete overrides it.
 *
 * Returns a change handler to use in place of the caller's own: it forwards
 * every change, and latches "manually edited" when the speed field itself is
 * touched.
 *
 * @param {object} params
 * @param {Array} params.fields         metric field template
 * @param {object} params.values        current metric values, keyed by field key
 * @param {Function} params.onChange    (key, value) => void
 * @param {boolean} [params.enabled]    set false when inputs are locked/read-only
 * @returns {{speedKey: ?string, isProposed: boolean, handleChange: Function}}
 */
export function useAvgSpeedProposal({ fields, values, onChange, enabled = true }) {
  const { distanceKey, durationKey, speedKey } = findSpeedFields(fields)
  const canPropose = Boolean(distanceKey && durationKey && speedKey)

  // Was the value currently sitting in the speed field put there by us?
  const proposedRef = useRef(null)
  // Has the athlete typed their own speed? Latches for the lifetime of the card.
  const manualRef = useRef(false)

  const current = speedKey ? values?.[speedKey] : undefined
  const distance = distanceKey ? values?.[distanceKey] : undefined
  const duration = durationKey ? values?.[durationKey] : undefined

  // A pre-existing value loaded from the server counts as the athlete's own.
  useEffect(() => {
    if (!canPropose) return
    if (proposedRef.current === null && current !== '' && current != null) {
      manualRef.current = true
    }
    // Deliberately runs on mount only — later values are attributed by the
    // handler below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPropose])

  useEffect(() => {
    if (!canPropose || !enabled || manualRef.current) return

    const proposal = computeAvgSpeedKmh(distance, duration)
    const isEmpty = current === '' || current == null
    const isOurs = proposedRef.current != null && String(current) === String(proposedRef.current)

    if (proposal === null) {
      // Distance or duration went away — retract our own proposal, leave
      // anything else alone.
      if (isOurs) {
        proposedRef.current = null
        onChange?.(speedKey, '')
      }
      return
    }

    if ((isEmpty || isOurs) && String(current) !== String(proposal)) {
      proposedRef.current = proposal
      onChange?.(speedKey, proposal)
    }
  }, [canPropose, enabled, distance, duration, current, speedKey, onChange])

  const handleChange = (key, value) => {
    if (canPropose && key === speedKey) {
      manualRef.current = true
      proposedRef.current = null
    }
    onChange?.(key, value)
  }

  const isProposed =
    canPropose && !manualRef.current && proposedRef.current != null &&
    String(current) === String(proposedRef.current)

  return { speedKey, isProposed, handleChange }
}
